import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { exportLabels } from '../constants/exportLabels';
import { Server } from '../services/Server';
import type { ExportRequest } from '../types';
import { handleError } from '../utils/Error';
import { exportErrorMessage } from '../utils/exportErrors';
import { readReportProblem } from '../utils/reportErrors';
import { triggerFileDownload } from '../utils/reports';

const exportTablesKey = (projectId: string) => ['export-tables', projectId];

const fallbackFilename = (request: ExportRequest): string => {
  if (request.format === 'xlsx') return 'SMT_study.xlsx';
  return request.tables.length > 1 ? 'SMT_csv.zip' : 'SMT_table.csv';
};

/** Tables the study can be exported as; loaded only while the choice is displayed. */
export const useExportTables = (projectId: string, enabled: boolean) => {
  const { getExportTablesAPI } = Server();
  return useQuery({
    queryKey: exportTablesKey(projectId),
    queryFn: ({ signal }) => getExportTablesAPI(projectId, signal),
    enabled,
    // The tables follow the stored results, which analyses change without saving the project.
    staleTime: 0,
    gcTime: 0,
  });
};

export const useDownloadExport = (projectId: string) => {
  const { downloadExportAPI } = Server();

  return useMutation({
    mutationFn: async (request: ExportRequest) => {
      const file = await downloadExportAPI(projectId, request);
      triggerFileDownload(file.blob, file.filename ?? fallbackFilename(request));
    },
    onSuccess: () => {
      toast.success(exportLabels.downloaded);
    },
    onError: async (error: unknown) => {
      const problem = await readReportProblem(error);
      if (problem.status === 401) {
        handleError(error);
        return;
      }
      const message = exportErrorMessage(problem);
      toast.error(message, { id: `export-error:${message}` });
    },
  });
};
